"use client";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarCirclePlus } from "@fortawesome/sharp-light-svg-icons";
import type { CaseData } from "@/cms/models";
import type { CaseLabelSettings } from "@/cms/case-labels";
import { getCaseDetailSublabel } from "@/utils/case-label";
import { newCaseLabelStyle } from "@/utils/case-label-style";
import { getOpenHouseText } from "@/utils/helper";
import { openhouseCalendar } from "@/utils/openhouse-calendar";
import { useCaseLabelSettings } from "./CaseLabelProvider";
import PropertyLabel from "./PropertyLabel";

export default function CaseDetailLabels({
  caseData,
  settings,
  inline = false,
}: {
  caseData: CaseData;
  settings?: CaseLabelSettings;
  inline?: boolean;
}) {
  const inherited = useCaseLabelSettings();
  const selected = settings ?? inherited;
  if (!newCaseLabelStyle(selected)) return null;
  const status = getCaseDetailSublabel(caseData);
  const event = caseData.isActive ? caseData.openhouses[0] : undefined;
  const title = event?.bookingRequired
    ? "Åbent hus med tilmelding"
    : "Åbent hus";
  const validTime =
    event &&
    Number.isFinite(new Date(event.startDate ?? "").getTime()) &&
    Number.isFinite(new Date(event.endDate ?? "").getTime());
  const date = validTime ? getOpenHouseText(event) : "";
  const registration =
    event && caseData.openhouses.some((item) => item.bookingRequired);
  if (!caseData.marketingTexts?.label && !status && !event) return null;
  return (
    <div
      className={
        inline
          ? "re-case-detail-labels re-case-detail-labels-inline"
          : "re-case-detail-labels"
      }
      data-case-detail-labels
    >
      {caseData.marketingTexts?.label && (
        <PropertyLabel settings={selected} positioned={false}>
          {caseData.marketingTexts.label}
        </PropertyLabel>
      )}
      {status && (
        <PropertyLabel settings={selected} positioned={false}>
          {status}
        </PropertyLabel>
      )}
      {event && (
        <PropertyLabel
          settings={selected}
          positioned={false}
          className="re-case-openhouse-label"
          news
        >
          <span className="re-case-openhouse-copy">
            <span>{title}</span>
            <span>{date}</span>
          </span>
          {validTime && (
            <button
              type="button"
              className="re-case-label-calendar"
              aria-label="Føj åbent hus til kalender"
              title="Føj åbent hus til kalender"
              onClick={() => {
                const content = openhouseCalendar(`${title} - ${date}`, event);
                if (!content) return;
                const url = URL.createObjectURL(
                  new Blob([content], { type: "text/calendar" }),
                );
                const link = document.createElement("a");
                link.href = url;
                link.download = `${caseData.advertisementAddressOrDisplayName}.ics`;
                link.click();
                URL.revokeObjectURL(url);
              }}
            >
              <FontAwesomeIcon icon={faCalendarCirclePlus} />
            </button>
          )}
          {registration && (
            <button
              type="button"
              className="re-case-label-register"
              onClick={() => {
                window.dispatchEvent(
                  new CustomEvent("selectedTabEvent", {
                    detail: { selectedTab: "openhouse" },
                  }),
                );
                (
                  document.getElementById("caseContactSection") ??
                  document.getElementById("caseTabsComponent")
                )?.scrollIntoView({ behavior: "smooth" });
              }}
            >
              Tilmeld
            </button>
          )}
        </PropertyLabel>
      )}
    </div>
  );
}
